// Função para formatar valores monetários
const formatCurrency = (value) => {
  if (typeof value !== "number") return "-";
  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

// Função para verificar se uma condição de pagamento permite desconto adicional
const paymentConditionAllowsDiscount = (paymentConditionCode, payments) => {
  if (!payments || !paymentConditionCode) return false;
  
  // Condição à vista sempre permite desconto de 5%
  if (paymentConditionCode === "003") return true;
  
  // Encontrar a descrição da condição de pagamento
  const payment = payments.find(p => p.E4_CODIGO === paymentConditionCode);
  if (!payment) return false;
  
  const prazoDescricao = payment.E4_DESCRI.trim();
  
  // Condições que permitem desconto conforme tabela
  const descontosPermitidos = {
    "21 DD": 4,
    "14/28 DD": 4,
    "14/21/28 DD": 4,
    "28 DD": 3,
    "21/35 DD": 3,
    "21/28/35 DD": 3,
    "35 DD": 2,
    "21/49 DD": 2,
    "28/42 DD": 2,
    "21/35/49 DD": 2,
    "42 DD": 1,
    "28/56 DD": 1,
    "28/42/56 DD": 1,
    "21/35/49/63 DD": 1
  };
  
  return descontosPermitidos.hasOwnProperty(prazoDescricao);
};

// Função para obter o desconto máximo permitido para uma condição de pagamento
const getMaxDiscountForPaymentCondition = (paymentConditionCode, payments) => {
  if (!payments || !paymentConditionCode) return 0;
  
  // Pagamento à vista (14 DD)
  if (paymentConditionCode === "003") return 5;
  
  // Encontrar a descrição da condição de pagamento
  const payment = payments.find(p => p.E4_CODIGO === paymentConditionCode);
  if (!payment) return 0;
  
  const prazoDescricao = payment.E4_DESCRI.trim();
  
  // Descontos conforme tabela
  const descontosPermitidos = {
    "21 DD": 4,
    "14/28 DD": 4,
    "14/21/28 DD": 4,
    "28 DD": 3,
    "21/35 DD": 3,
    "21/28/35 DD": 3,
    "35 DD": 2,
    "21/49 DD": 2,
    "28/42 DD": 2,
    "21/35/49 DD": 2,
    "42 DD": 1,
    "28/56 DD": 1,
    "28/42/56 DD": 1,
    "21/35/49/63 DD": 1
  };
  
  return descontosPermitidos[prazoDescricao] || 0;
};

// Função para verificar se uma condição de pagamento é válida para um valor de pedido
const isPaymentConditionValidForValue = (paymentConditionCode, payments, orderValue) => {
  if (!payments || !paymentConditionCode) return true;
  
  // Encontrar a descrição da condição de pagamento
  const payment = payments.find(p => p.E4_CODIGO === paymentConditionCode);
  if (!payment) return true;
  
  const prazoDescricao = payment.E4_DESCRI.trim();
  
  // Códigos especiais para pagamento à vista
  if (paymentConditionCode === "003") {
    // 14 DD - Pagamento à vista, permitido para qualquer valor
    return true;
  }
  
  // Condições com prazo de 21 dias (somente 21 dd, 14/28 dd ou 14/21/28 dd)
  if (prazoDescricao === "21 DD" || prazoDescricao === "14/28 DD" || prazoDescricao === "14/21/28 DD") {
    // Requer valor mínimo de R$ 1.200,00
    if (orderValue < 1200) {
      return { valid: false, minValue: 1200, description: prazoDescricao };
    }
    return true;
  }
  
  // Condições com prazo de 28 dias (28 dd, 21/35 dd ou 21/28/35 dd)
  if (prazoDescricao === "28 DD" || prazoDescricao === "21/35 DD" || prazoDescricao === "21/28/35 DD") {
    // Requer valor mínimo de R$ 1.200,00
    if (orderValue < 1200) {
      return { valid: false, minValue: 1200, description: prazoDescricao };
    }
    return true;
  }
  
  // Condições com prazo inicial de 35 a 42 dias
  if (prazoDescricao.startsWith("35 DD") || prazoDescricao.startsWith("21/49 DD") || 
      prazoDescricao.startsWith("28/42 DD") || prazoDescricao.startsWith("21/35/49 DD") ||
      prazoDescricao.startsWith("42 DD") || prazoDescricao.startsWith("28/56 DD") ||
      prazoDescricao.startsWith("28/42/56 DD") || prazoDescricao.startsWith("21/35/49/63 DD")) {
    // Requer valor mínimo de R$ 2.000,00
    if (orderValue < 2000) {
      return { valid: false, minValue: 2000, description: prazoDescricao };
    }
    return true;
  }
  
  // Condições com prazos mais longos (49 dias ou mais)
  if (prazoDescricao.startsWith("49 DD") || prazoDescricao.startsWith("35/63 DD") || 
      prazoDescricao.startsWith("35/49/63 DD") || prazoDescricao.startsWith("56 DD") ||
      prazoDescricao.startsWith("42/70 DD") || prazoDescricao.startsWith("42/56/70 DD") ||
      prazoDescricao.startsWith("35/49/63/77 DD")) {
    // Requer valor mínimo de R$ 3.000,00
    if (orderValue < 3000) {
      return { valid: false, minValue: 3000, description: prazoDescricao };
    }
    return true;
  }
  
  // Se não se encaixar em nenhuma regra específica, permite
  return true;
};

// ValidateOrder.js
export function validateOrder(order, config, { skipMinValueCheck = false, payments = [] } = {}) {
  // Caso a configuração não tenha sido carregada, podemos pular as validações customizadas.
  if (!config) {
    return null;
  }

  // Utiliza a primeira condição de venda disponível na configuração
  const condicaoVenda = config.condicoes_venda && config.condicoes_venda[0];

  // Usa diretamente o estado do cliente do pedido
  const clientState = order.customerState;
  if (!clientState) {
    return "Estado do cliente não informado.";
  }

  // Calcula o valor total do pedido (sem descontos)
  const orderValue = order.items.reduce(
    (acc, item) => acc + item.quantity * item.price,
    0
  );

  // Calcula o valor total após todos os descontos
  const itemsDiscount = order.items.reduce((acc, item) => {
    const itemTotal = item.quantity * item.price;
    return acc + itemTotal * (parseFloat(item.discount) / 100);
  }, 0);

  // --- Validação de Desconto Adicional ---
  // Verificamos se o desconto adicional está de acordo com a condição de pagamento
  let additionalDiscountIsValid = false;
  
  if (!order.isSupervised && order.cashDiscount > 0) {
    const allowsDiscount = paymentConditionAllowsDiscount(order.paymentCondition, payments);
    
    if (!allowsDiscount) {
      return `A condição de pagamento selecionada não permite desconto adicional.`;
    }
    
    const maxDiscount = getMaxDiscountForPaymentCondition(order.paymentCondition, payments);
    
    if (order.cashDiscount > maxDiscount) {
      return `O desconto adicional não pode exceder ${maxDiscount}% para a forma de pagamento selecionada.`;
    }
    
    // Desconto adicional está válido para a condição de pagamento
    additionalDiscountIsValid = true;
  }

  const additionalDiscount = order.cashDiscount > 0
    ? (orderValue - itemsDiscount) * (parseFloat(order.cashDiscount) / 100)
    : 0;
    
  const finalOrderValue = orderValue - itemsDiscount - additionalDiscount;

  // Determina o valor mínimo de pedido baseado no estado do cliente
  let minOrderValue = condicaoVenda?.duplicata_minima || 600.0;
  
  // Verifica se temos configuração de frete CIF por estado
  if (condicaoVenda?.frete_cif?.estados && condicaoVenda.frete_cif.estados[clientState]) {
    minOrderValue = condicaoVenda.frete_cif.estados[clientState];
  }

  // VALIDAÇÃO ABSOLUTA DO VALOR MÍNIMO POR REGIÃO
  if (!skipMinValueCheck && !order.isSupervised) {
    // Para validação do valor mínimo, sempre usamos o valor final após todos os descontos
    if (finalOrderValue < minOrderValue) {
      const totalDiscount = itemsDiscount + additionalDiscount;
      const discountPercentage = (totalDiscount / orderValue * 100).toFixed(2);
      
      // Se o desconto adicional for válido, mas causou valor abaixo do mínimo, fornecemos uma mensagem específica
      if (additionalDiscountIsValid && additionalDiscount > 0) {
        return `BLOQUEIO POR VALOR MÍNIMO DA REGIÃO\n\nO valor final do pedido (${formatCurrency(finalOrderValue)}) está abaixo do mínimo exigido de ${formatCurrency(minOrderValue)} para o estado ${clientState}.\n\nEmbora o desconto adicional de ${order.cashDiscount}% seja permitido para esta condição de pagamento, ele não pode reduzir o valor total abaixo do mínimo para a região.\n\nDetalhamento:\n- Valor bruto: ${formatCurrency(orderValue)}\n- Desconto de itens: ${formatCurrency(itemsDiscount)}\n- Desconto adicional: ${formatCurrency(additionalDiscount)}\n- Valor final: ${formatCurrency(finalOrderValue)}\n\nO valor FINAL após todos os descontos DEVE ser igual ou superior a ${formatCurrency(minOrderValue)}.\n\nAjuste os descontos ou adicione mais itens para atingir o valor mínimo exigido.`;
      }
      
      // Mensagem padrão para outros casos
      return `BLOQUEIO POR VALOR MÍNIMO DA REGIÃO\n\nO valor final do pedido (${formatCurrency(finalOrderValue)}) está abaixo do mínimo exigido de ${formatCurrency(minOrderValue)} para o estado ${clientState}.\n\nDetalhamento:\n- Valor bruto: ${formatCurrency(orderValue)}\n- Total de descontos: ${formatCurrency(totalDiscount)} (${discountPercentage}%)\n- Valor final: ${formatCurrency(finalOrderValue)}\n\nO valor FINAL após todos os descontos DEVE ser igual ou superior a ${formatCurrency(minOrderValue)}.\n\nAjuste os descontos ou adicione mais itens para atingir o valor mínimo exigido.`;
    }

    // Verifica o valor mínimo da condição de pagamento, também usando o valor final
    if (order.paymentCondition) {
      const paymentConditionValid = isPaymentConditionValidForValue(order.paymentCondition, payments, finalOrderValue);
      if (paymentConditionValid !== true) {
        return `BLOQUEIO POR VALOR MÍNIMO DA CONDIÇÃO DE PAGAMENTO\n\nA condição de pagamento "${paymentConditionValid.description}" exige um valor mínimo de ${formatCurrency(paymentConditionValid.minValue)}.\n\nDetalhamento:\n- Valor final atual: ${formatCurrency(finalOrderValue)}\n- Valor mínimo exigido: ${formatCurrency(paymentConditionValid.minValue)}\n\nAjuste os descontos ou adicione mais itens para atingir o valor mínimo exigido.`;
      }
    }
  }

  // VALIDAÇÃO SECUNDÁRIA - Condição de Pagamento
  let minValueByPaymentCondition = 0;
  if (!order.isSupervised && order.paymentCondition) {
    const paymentConditionValid = isPaymentConditionValidForValue(order.paymentCondition, payments, finalOrderValue);
    
    if (paymentConditionValid !== true) {
      minValueByPaymentCondition = paymentConditionValid.minValue;
      if (finalOrderValue < minValueByPaymentCondition) {
        return `A condição de pagamento "${paymentConditionValid.description}" exige um valor mínimo de ${formatCurrency(minValueByPaymentCondition)}. Valor final após descontos: ${formatCurrency(finalOrderValue)}.`;
      }
    }
  }

  // --- Validação de Desconto por Item ---
  let allowedDiscount = 0;
  
  if (order.isSupervised) {
    allowedDiscount = 15;
  } else if (condicaoVenda?.reguladores_pressao_gas?.descontos) {
    const discountRules = condicaoVenda.reguladores_pressao_gas.descontos;
    // Usa finalOrderValue para determinar a faixa de desconto permitida
    for (const rule of discountRules) {
      if (finalOrderValue >= rule.valor_pedido) {
        allowedDiscount = rule.percentual;
      }
    }
  }

  // Para validação do percentual máximo de desconto por item, verificamos apenas o desconto no item
  // sem considerar o desconto adicional (que é ligado à condição de pagamento)
  for (const item of order.items) {
    if (item.quantity > 0 && item.discount > allowedDiscount) {
      return `O desconto do item "${item.product}" excede o limite de ${allowedDiscount.toFixed(2)}% permitido para o valor total do pedido de ${formatCurrency(orderValue)}.`;
    }
  }

  // --- Validação de Quantidade em Múltiplos de 5 ---
  for (const item of order.items) {
    if (item.quantity % 5 !== 0) {
      return `A quantidade do produto "${item.product}" deve ser um múltiplo de 5.`;
    }
  }

  return null;
}
